import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { LayoutDashboard, HardHat, DollarSign, Users, Warehouse, BookOpen, BarChart3, ShieldCheck, Check, Download, ArrowRight, Smartphone, Zap, Target, Building2, PieChart, FileText, TrendingUp, Calendar } from 'lucide-react';
import WebsiteNav from './WebsiteNav';
import WebsiteFooter from './WebsiteFooter';

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };
const stagger = { visible: { transition: { staggerChildren: 0.1 } } };

const FEATURES = [
  { icon: LayoutDashboard, title: 'Planning', desc: 'Projects, WBS, milestones, tasks & teams in one place.', color: '#3B82F6', img: '/screenshots/dashboard.jpg' },
  { icon: HardHat, title: 'Execution', desc: 'Daily logs, manpower, materials & team reports.', color: '#10B981', img: '/screenshots/teams.jpg' },
  { icon: DollarSign, title: 'Finance', desc: 'Budgets, costs, invoices, payments & variations.', color: '#F59E0B', img: '/screenshots/financial-dashboard.jpg' },
  { icon: Users, title: 'Payroll & HR', desc: 'Employees, salary structures, payslips & EOS.', color: '#8B5CF6', img: '/screenshots/payroll.jpg' },
  { icon: Warehouse, title: 'Inventory', desc: 'Warehouses, POs, stock movements & suppliers.', color: '#06B6D4', img: '/screenshots/inventory.jpg' },
  { icon: BookOpen, title: 'Accounting', desc: 'Chart of Accounts, GL, Trial Balance, P&L.', color: '#EC4899', img: '/screenshots/accounting.jpg' },
  { icon: BarChart3, title: 'Reports', desc: 'Weekly, executive & advanced analytics.', color: '#F97316', img: '/screenshots/reports.jpg' },
  { icon: ShieldCheck, title: 'Administration', desc: 'RBAC, invites, multi-tenant & audit trail.', color: '#6366F1', img: '/screenshots/role-permissions.jpg' },
];

const WHY_SUPERIOR = [
  { icon: Zap, title: 'Minimum Input, Maximum Output', desc: 'Smart automation derives costs from execution data, generates journal entries, and auto-creates reports. Enter once, benefit everywhere.' },
  { icon: Smartphone, title: 'Mobile Responsive', desc: 'Access your projects from any device — desktop, tablet, or phone. Full functionality on the go, no app download needed.' },
  { icon: Target, title: 'All-in-One Platform', desc: 'From project planning to P&L accounts — no need for 5 different tools. One login, one platform, complete control.' },
  { icon: FileText, title: 'Client-Ready Presentations', desc: 'Export professional PDF reports, financial statements, and project dashboards. Present project data to clients with confidence.' },
  { icon: Building2, title: 'Multi-Project Management', desc: 'Run unlimited projects simultaneously. Compare performance, allocate resources, and track finances across your entire portfolio.' },
  { icon: PieChart, title: 'Real-Time Financial Insight', desc: 'Budget vs Actual, Profit & Loss, Balance Sheet, and Cash Flow — always up to date with double-entry accounting.' },
];

const USE_CASES = [
  { title: 'Commercial Buildings', desc: 'Offices, malls, hotels and mixed-use developments.', img: 'https://images.unsplash.com/photo-1770821030454-5e3ccb2d96dc?w=600&q=80' },
  { title: 'Residential Projects', desc: 'Villas, apartments and gated community developments.', img: 'https://images.unsplash.com/photo-1599995903128-531fc7fb694b?w=600&q=80' },
  { title: 'Industrial & Infrastructure', desc: 'Factories, plants, roads and utility projects.', img: 'https://images.pexels.com/photos/2383650/pexels-photo-2383650.jpeg?auto=compress&w=600' },
  { title: 'Renovation & Fit-Out', desc: 'Interior fit-outs, refurbishments and restorations.', img: 'https://images.unsplash.com/photo-1768321903466-313ed74cbb62?w=600&q=80' },
];

function AnimatedCounter({ end, suffix = '', duration = 2000 }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-50px' });

  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const step = Math.max(1, Math.floor(end / (duration / 30)));
    const timer = setInterval(() => {
      start += step;
      if (start >= end) { setCount(end); clearInterval(timer); }
      else setCount(start);
    }, 30);
    return () => clearInterval(timer);
  }, [inView, end, duration]);

  return <span ref={ref}>{inView ? count : 0}{suffix}</span>;
}

// ==================== ANIMATED GROWTH CHARTS ====================

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const PROJECT_DATA = [12, 28, 45, 68, 95, 130, 172, 218, 270, 328, 395, 470];
const REVENUE_DATA = [0.2, 0.5, 0.9, 1.4, 2.0, 2.8, 3.7, 4.8, 6.1, 7.6, 9.4, 11.5];

function AnimatedLineChart({ data, color, label, maxVal }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  const w = 400, h = 200, padX = 40, padY = 30;
  const plotW = w - padX * 2, plotH = h - padY * 2;

  const points = data.map((v, i) => ({
    x: padX + (i / (data.length - 1)) * plotW,
    y: padY + plotH - (v / maxVal) * plotH,
  }));
  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
  const areaD = pathD + ` L ${points[points.length - 1].x} ${padY + plotH} L ${points[0].x} ${padY + plotH} Z`;

  return (
    <div ref={ref}>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full" style={{ overflow: 'visible' }}>
        <defs>
          <linearGradient id={`grad-${label}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.3" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        {/* Grid lines */}
        {[0, 0.25, 0.5, 0.75, 1].map((r, i) => (
          <line key={i} x1={padX} y1={padY + plotH * (1 - r)} x2={padX + plotW} y2={padY + plotH * (1 - r)} stroke="#1E293B" strokeWidth="1" />
        ))}
        {/* X axis labels */}
        {MONTHS.map((m, i) => (
          <text key={m} x={padX + (i / 11) * plotW} y={h - 5} fill="#475569" fontSize="9" textAnchor="middle" fontFamily="Manrope">{m}</text>
        ))}
        {/* Area fill */}
        <path d={areaD} fill={`url(#grad-${label})`}
          style={{ opacity: inView ? 1 : 0, transition: 'opacity 1s ease 0.5s' }} />
        {/* Line */}
        <path d={pathD} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
          style={{
            strokeDasharray: 1200,
            strokeDashoffset: inView ? 0 : 1200,
            transition: 'stroke-dashoffset 2s ease-in-out',
          }} />
        {/* Dot markers */}
        {points.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r="3" fill={color}
            style={{
              opacity: inView ? 1 : 0,
              transition: `opacity 0.3s ease ${0.15 * i + 0.5}s`,
            }} />
        ))}
        {/* End value label */}
        <text x={points[points.length - 1].x + 6} y={points[points.length - 1].y + 4} fill={color} fontSize="11" fontWeight="700" fontFamily="Outfit"
          style={{ opacity: inView ? 1 : 0, transition: 'opacity 0.5s ease 2.2s' }}>
          {data[data.length - 1]}{label === 'revenue' ? 'M' : ''}
        </text>
      </svg>
    </div>
  );
}

function AnimatedBarChart({ data, color, maxVal }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  const w = 400, h = 200, padX = 40, padY = 30;
  const plotW = w - padX * 2, plotH = h - padY * 2;
  const barW = plotW / data.length * 0.6;
  const gap = plotW / data.length;

  return (
    <div ref={ref}>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full">
        <defs>
          <linearGradient id="bar-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} />
            <stop offset="100%" stopColor={color} stopOpacity="0.4" />
          </linearGradient>
        </defs>
        {/* Grid lines */}
        {[0, 0.25, 0.5, 0.75, 1].map((r, i) => (
          <line key={i} x1={padX} y1={padY + plotH * (1 - r)} x2={padX + plotW} y2={padY + plotH * (1 - r)} stroke="#1E293B" strokeWidth="1" />
        ))}
        {/* X axis labels */}
        {MONTHS.map((m, i) => (
          <text key={m} x={padX + i * gap + gap / 2} y={h - 5} fill="#475569" fontSize="9" textAnchor="middle" fontFamily="Manrope">{m}</text>
        ))}
        {/* Bars */}
        {data.map((v, i) => {
          const barH = (v / maxVal) * plotH;
          return (
            <rect key={i}
              x={padX + i * gap + (gap - barW) / 2}
              y={padY + plotH - (inView ? barH : 0)}
              width={barW}
              height={inView ? barH : 0}
              rx="3"
              fill="url(#bar-grad)"
              style={{ transition: `all 0.5s ease ${i * 0.1}s` }}
            />
          );
        })}
      </svg>
    </div>
  );
}

// ==================== MAIN PAGE ====================

export default function HomePage() {
  return (
    <div className="bg-[#0B0F19] min-h-screen text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <WebsiteNav />

      {/* HERO with animated background */}
      <section className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero-section">
        {/* Animated Ken Burns background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 animate-ken-burns">
            <img
              src="https://images.unsplash.com/photo-1758399750112-366c3c804eb7?w=1920&q=80"
              alt=""
              className="w-full h-full object-cover scale-110"
            />
          </div>
          {/* Dark cinematic overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#0B0F19] via-[#0B0F19]/85 to-[#0B0F19]/50" />
          {/* Bottom gradient fade */}
          <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-[#0B0F19] to-transparent" />
          {/* Warm sunset tint */}
          <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-[#F59E0B]/8" />
        </div>

        {/* Floating particles */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="absolute rounded-full bg-white/5 animate-float-particle"
              style={{
                width: `${4 + i * 3}px`, height: `${4 + i * 3}px`,
                left: `${15 + i * 14}%`, top: `${20 + (i % 3) * 25}%`,
                animationDelay: `${i * 1.5}s`, animationDuration: `${8 + i * 2}s`,
              }} />
          ))}
        </div>

        <div className="absolute top-32 right-20 w-96 h-96 bg-orange-500/10 rounded-full blur-[120px] pointer-events-none hidden md:block" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-32">
          <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-3xl">
            <motion.p variants={fadeUp} className="text-xs font-bold tracking-[0.25em] uppercase text-amber-400 mb-5">
              Project ERP & Control System
            </motion.p>
            <motion.h1 variants={fadeUp} className="text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tight leading-none mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              PLAN.<br />BUILD.<br />DELIVER.
            </motion.h1>
            <motion.p variants={fadeUp} className="text-base sm:text-lg text-slate-300 leading-relaxed max-w-xl mb-10">
              The all-in-one ERP platform built for construction companies. Manage projects, teams, finances, payroll, inventory, and accounting — from foundation to handover.
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-wrap gap-4">
              <Link to="/website/features" className="px-7 py-3.5 bg-[#3B82F6] hover:bg-[#2563EB] text-white font-semibold rounded-lg transition-all text-sm shadow-lg shadow-blue-500/25" data-testid="hero-explore-btn">
                Explore Features
              </Link>
              <Link to="/demo" className="px-7 py-3.5 bg-white/5 hover:bg-white/10 border border-white/15 text-white font-semibold rounded-lg transition-all text-sm" data-testid="hero-demo-btn">
                Try Live Demo
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* INTRO STATS */}
      <section className="py-16 sm:py-20 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8 text-center">
            {[
              { end: 30, suffix: '+', label: 'Modules' },
              { end: 11, suffix: '', label: 'User Roles' },
              { end: 100, suffix: '%', label: 'Data Isolation' },
              { end: 0, suffix: '', label: 'Tax Compliant', text: 'UAE' },
            ].map((s, i) => (
              <motion.div key={i} initial={{ opacity: 0, scale: 0.5 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}
                transition={{ delay: i * 0.12, type: 'spring', stiffness: 200, damping: 15 }}>
                <p className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {s.text ? s.text : <AnimatedCounter end={s.end} suffix={s.suffix} />}
                </p>
                <p className="text-xs sm:text-sm text-slate-500">{s.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* GROWTH METRICS — Animated Charts */}
      <section className="py-20 md:py-28 bg-[#070A12] relative overflow-hidden" data-testid="growth-charts-section">
        <div className="absolute top-0 left-1/3 w-96 h-96 bg-blue-500/5 rounded-full blur-[150px] pointer-events-none" />
        <div className="absolute bottom-0 right-1/4 w-72 h-72 bg-emerald-500/5 rounded-full blur-[120px] pointer-events-none" />

        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-14">
            <motion.p variants={fadeUp} className="text-xs font-bold tracking-[0.2em] uppercase text-emerald-400 mb-3">Performance Metrics</motion.p>
            <motion.h2 variants={fadeUp} className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Driving Construction Growth
            </motion.h2>
            <motion.p variants={fadeUp} className="text-slate-400 mt-3 max-w-2xl mx-auto text-sm sm:text-base">
              Real-time analytics tracking project delivery and revenue across your portfolio.
            </motion.p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Project Delivery Chart */}
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}>
              <div className="bg-[#0F1420] border border-white/8 rounded-2xl p-6 hover:border-white/15 transition-colors" data-testid="project-delivery-chart">
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[#3B82F6]/10 flex items-center justify-center">
                      <Calendar size={20} className="text-[#3B82F6]" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>Project Delivery</h3>
                      <p className="text-[10px] text-slate-500">Cumulative milestones achieved</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-[#3B82F6]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      <AnimatedCounter end={470} suffix="" />
                    </p>
                    <p className="text-[10px] text-emerald-400">+38% YoY</p>
                  </div>
                </div>
                <AnimatedLineChart data={PROJECT_DATA} color="#3B82F6" label="projects" maxVal={500} />
              </div>
            </motion.div>

            {/* Revenue Growth Chart */}
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.25 }}>
              <div className="bg-[#0F1420] border border-white/8 rounded-2xl p-6 hover:border-white/15 transition-colors" data-testid="revenue-growth-chart">
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[#10B981]/10 flex items-center justify-center">
                      <TrendingUp size={20} className="text-[#10B981]" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>Revenue Managed</h3>
                      <p className="text-[10px] text-slate-500">Monthly revenue tracked ($M)</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-[#10B981]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      $<AnimatedCounter end={11} suffix=".5M" />
                    </p>
                    <p className="text-[10px] text-emerald-400">+52% YoY</p>
                  </div>
                </div>
                <AnimatedBarChart data={REVENUE_DATA} color="#10B981" maxVal={13} />
              </div>
            </motion.div>
          </div>

          {/* Bottom metric cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
            {[
              { value: '247', label: 'Projects Delivered', color: '#3B82F6' },
              { value: '$2.4B', label: 'Revenue Managed', color: '#10B981' },
              { value: '99.8%', label: 'Uptime', color: '#F59E0B' },
              { value: '1,200+', label: 'Active Users', color: '#8B5CF6' },
            ].map((m, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.3 + i * 0.08 }}>
                <div className="bg-[#0F1420] border border-white/5 rounded-xl p-4 text-center hover:border-white/10 transition-colors">
                  <p className="text-xl font-bold mb-0.5" style={{ color: m.color, fontFamily: 'Outfit, sans-serif' }}>{m.value}</p>
                  <p className="text-[10px] text-slate-500">{m.label}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* APP INTRODUCTION */}
      <section className="py-20 md:py-28" data-testid="intro-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
              <motion.p variants={fadeUp} className="text-xs font-bold tracking-[0.25em] uppercase text-yellow-500 mb-3">About the Platform</motion.p>
              <motion.h2 variants={fadeUp} className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight leading-tight mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                One Platform to Run Your Entire Construction Business
              </motion.h2>
              <motion.p variants={fadeUp} className="text-slate-400 leading-relaxed mb-4">
                <strong className="text-slate-200">ProjectBook Pro&trade;</strong> is a comprehensive Project ERP & Control System purpose-built for the construction industry. It replaces the spreadsheets, disconnected tools, and manual processes that slow down your projects.
              </motion.p>
              <motion.p variants={fadeUp} className="text-slate-400 leading-relaxed mb-6">
                From the moment you create a project to the final financial close-out, every piece of data flows through one unified system. Site teams log daily progress, finance tracks costs and invoices, HR manages payroll, and management gets real-time dashboards — all connected, all in sync.
              </motion.p>
              <motion.div variants={fadeUp}>
                <Link to="/website/features" className="inline-flex items-center gap-2 text-sm font-semibold text-[#3B82F6] hover:text-[#60A5FA] transition-colors">
                  See All Features <ArrowRight size={16} />
                </Link>
              </motion.div>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }}>
              <div className="bg-[#1A1F36] border border-white/10 rounded-xl overflow-hidden shadow-2xl">
                <div className="flex items-center gap-1.5 px-4 py-2 bg-[#141827] border-b border-white/5">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
                  <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/60" />
                  <div className="w-2.5 h-2.5 rounded-full bg-green-500/60" />
                </div>
                <img src="/screenshots/dashboard.jpg" alt="Dashboard" className="w-full" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* WHY SUPERIOR */}
      <section className="py-20 md:py-28 bg-[#0E1220]" data-testid="why-superior-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-14">
            <motion.p variants={fadeUp} className="text-xs font-bold tracking-[0.2em] uppercase text-blue-400 mb-3">Why Choose ProjectBook Pro</motion.p>
            <motion.h2 variants={fadeUp} className="text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              User Friendly. Powerful. Complete.
            </motion.h2>
            <motion.p variants={fadeUp} className="text-slate-400 mt-3 max-w-2xl mx-auto text-sm sm:text-base">
              Designed for people who run construction projects — not for IT departments.
            </motion.p>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {WHY_SUPERIOR.map((w, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}>
                <div className="bg-[#1A1F36] border border-white/8 rounded-xl p-6 h-full transition-all duration-300 hover:-translate-y-1 hover:border-white/20" data-testid={`why-card-${i}`}>
                  <div className="w-10 h-10 rounded-xl bg-[#3B82F6]/10 flex items-center justify-center mb-4">
                    <w.icon size={20} className="text-[#3B82F6]" />
                  </div>
                  <h3 className="font-semibold text-sm text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{w.title}</h3>
                  <p className="text-xs text-slate-500 leading-relaxed">{w.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES OVERVIEW */}
      <section className="py-20 md:py-28" data-testid="features-overview">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-14">
            <motion.p variants={fadeUp} className="text-xs font-bold tracking-[0.2em] uppercase text-blue-400 mb-3">Complete ERP Suite</motion.p>
            <motion.h2 variants={fadeUp} className="text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Everything You Need, One Platform
            </motion.h2>
            <motion.p variants={fadeUp} className="text-slate-400 mt-3 max-w-2xl mx-auto text-sm sm:text-base">
              Eight powerful modules covering every aspect of construction operations.
            </motion.p>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {FEATURES.map((f, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}>
                <Link to="/website/features" className="block group" data-testid={`feature-card-${i}`}>
                  <div className="bg-[#1A1F36] border border-white/8 rounded-xl overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:border-white/20 hover:shadow-2xl">
                    <div className="h-40 overflow-hidden">
                      <img src={f.img} alt={f.title} className="w-full h-full object-cover opacity-70 group-hover:opacity-90 transition-opacity duration-300" />
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-2.5 mb-2">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: f.color + '18' }}>
                          <f.icon size={16} style={{ color: f.color }} />
                        </div>
                        <h3 className="font-semibold text-sm text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>{f.title}</h3>
                      </div>
                      <p className="text-xs text-slate-500 leading-relaxed">{f.desc}</p>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SCREENSHOT SHOWCASE */}
      <section className="py-20 md:py-28 bg-[#0E1220]" data-testid="screenshot-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-14">
            <motion.p variants={fadeUp} className="text-xs font-bold tracking-[0.2em] uppercase text-yellow-500 mb-3">See It In Action</motion.p>
            <motion.h2 variants={fadeUp} className="text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Powerful Dashboards & Reports
            </motion.h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { img: '/screenshots/dashboard.jpg', title: 'Project Dashboard', desc: 'Real-time KPIs across all projects' },
              { img: '/screenshots/financial-dashboard.jpg', title: 'Financial Dashboard', desc: 'Revenue, costs, profit margins & cash flow' },
              { img: '/screenshots/accounting.jpg', title: 'Accounting Ledgers', desc: 'Chart of Accounts, GL, Trial Balance, P&L' },
              { img: '/screenshots/role-permissions.jpg', title: 'Role Permissions', desc: '11 roles with editable access matrix' },
            ].map((s, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="group">
                <div className="bg-[#1A1F36] border border-white/8 rounded-xl overflow-hidden transition-all duration-300 hover:border-white/20">
                  <div className="overflow-hidden"><img src={s.img} alt={s.title} className="w-full object-cover group-hover:scale-[1.02] transition-transform duration-500" /></div>
                  <div className="p-4">
                    <h4 className="text-sm font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>{s.title}</h4>
                    <p className="text-xs text-slate-500 mt-0.5">{s.desc}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* WHERE IT CAN BE USED */}
      <section className="py-20 md:py-28" data-testid="use-cases-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-14">
            <motion.p variants={fadeUp} className="text-xs font-bold tracking-[0.2em] uppercase text-blue-400 mb-3">Built For Construction</motion.p>
            <motion.h2 variants={fadeUp} className="text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Where It Can Be Used
            </motion.h2>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {USE_CASES.map((u, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="group" data-testid={`use-case-${i}`}>
                <div className="bg-[#1A1F36] border border-white/8 rounded-xl overflow-hidden h-full transition-all duration-300 hover:-translate-y-1 hover:border-white/20">
                  <div className="h-44 overflow-hidden"><img src={u.img} alt={u.title} className="w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-all duration-500" /></div>
                  <div className="p-5">
                    <h3 className="font-semibold text-sm text-white mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{u.title}</h3>
                    <p className="text-xs text-slate-500 leading-relaxed">{u.desc}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURE SUMMARY */}
      <section className="py-20 md:py-28 bg-[#0E1220]" data-testid="feature-summary-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-14">
            <motion.p variants={fadeUp} className="text-xs font-bold tracking-[0.2em] uppercase text-yellow-500 mb-3">Feature Summary</motion.p>
            <motion.h2 variants={fadeUp} className="text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Everything at a Glance
            </motion.h2>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3 max-w-5xl mx-auto">
            {[
              'Project lifecycle management', 'Work Breakdown Structure (WBS)', 'Milestones, tasks & subtasks',
              'Daily site logs & team reports', 'Manpower & material tracking', 'Productivity analysis',
              'Budget management & cost tracking', 'Invoice & payment management', 'Variations & change orders',
              'Full payroll with payslips', 'Inventory & warehouse management', 'Purchase order processing',
              'Double-entry accounting', 'Trial Balance, P&L, Balance Sheet', 'VAT/GST compliance reports',
              'Weekly & executive reports', 'Document management & approvals', 'Calendar & meeting notes',
              'Role-based access (11 roles)', 'User invitation system', 'Multi-tenant data isolation',
              'Dark & light theme', 'Audit trail & login logs', 'Subscription management',
              'PDF & Excel exports', 'Real-time notifications', 'Mobile responsive design',
            ].map((f, i) => (
              <motion.div key={i} initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.02 }}
                className="flex items-center gap-2.5 py-2">
                <Check size={14} className="text-[#10B981] shrink-0" />
                <span className="text-sm text-slate-300">{f}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* DOWNLOAD SECTION */}
      <section className="py-20 md:py-28" data-testid="download-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-14">
            <motion.p variants={fadeUp} className="text-xs font-bold tracking-[0.2em] uppercase text-blue-400 mb-3">Resources</motion.p>
            <motion.h2 variants={fadeUp} className="text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Download & Learn More
            </motion.h2>
            <motion.p variants={fadeUp} className="text-slate-400 mt-3 max-w-xl mx-auto text-sm sm:text-base">
              Get detailed documentation about ProjectBook Pro&trade; features, setup, and usage.
            </motion.p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <div className="bg-gradient-to-br from-[#1A1F36] to-[#0E1220] border border-white/10 rounded-xl p-6 sm:p-8 h-full transition-all hover:border-[#3B82F6]/30" data-testid="download-user-guide">
                <div className="w-12 h-12 rounded-xl bg-[#3B82F6]/10 flex items-center justify-center mb-5">
                  <BookOpen size={24} className="text-[#3B82F6]" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>User Guide</h3>
                <p className="text-sm text-slate-400 leading-relaxed mb-5">
                  Complete module-by-module documentation with 36 modules explained, 18 FAQs answered, and step-by-step guides for every feature.
                </p>
                <div className="flex flex-wrap gap-2 mb-6">
                  {['36 Modules', '18 FAQs', 'Searchable', 'PDF Export'].map(t => (
                    <span key={t} className="text-[10px] font-medium text-slate-400 border border-white/10 rounded-full px-2.5 py-0.5">{t}</span>
                  ))}
                </div>
                <Link to="/demo" className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#3B82F6] hover:bg-[#2563EB] text-white text-sm font-semibold rounded-lg transition-all shadow-lg shadow-blue-500/20">
                  <Download size={16} /> Access User Guide
                </Link>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}>
              <div className="bg-gradient-to-br from-[#1A1F36] to-[#0E1220] border border-white/10 rounded-xl p-6 sm:p-8 h-full transition-all hover:border-[#F59E0B]/30" data-testid="download-training-manual">
                <div className="w-12 h-12 rounded-xl bg-[#F59E0B]/10 flex items-center justify-center mb-5">
                  <FileText size={24} className="text-[#F59E0B]" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Training Manual</h3>
                <p className="text-sm text-slate-400 leading-relaxed mb-5">
                  Professional marketing & training PDF with cover page, all 45 features documented across 8 modules, FAQ, and SGK Apps branding.
                </p>
                <div className="flex flex-wrap gap-2 mb-6">
                  {['45 Features', '8 Modules', 'Branded PDF', 'Print Ready'].map(t => (
                    <span key={t} className="text-[10px] font-medium text-slate-400 border border-white/10 rounded-full px-2.5 py-0.5">{t}</span>
                  ))}
                </div>
                <Link to="/demo" className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#F59E0B] hover:bg-[#D97706] text-black text-sm font-semibold rounded-lg transition-all shadow-lg shadow-yellow-500/20">
                  <Download size={16} /> Access Training Manual
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA BANNER */}
      <section className="py-20 md:py-28 bg-[#0E1220] relative overflow-hidden" data-testid="cta-section">
        <div className="absolute top-10 left-1/4 w-72 h-72 bg-blue-500/15 rounded-full blur-[100px] pointer-events-none" />
        <div className="absolute bottom-10 right-1/4 w-72 h-72 bg-yellow-500/10 rounded-full blur-[100px] pointer-events-none" />
        <div className="relative max-w-3xl mx-auto px-6 text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.h2 variants={fadeUp} className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Ready to Transform Your Projects?
            </motion.h2>
            <motion.p variants={fadeUp} className="text-slate-400 mb-8 max-w-lg mx-auto text-sm sm:text-base">
              Experience the full power of ProjectBook Pro&trade; with a live demo. No credit card required.
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-col sm:flex-row justify-center gap-4">
              <Link to="/demo" className="px-8 py-3.5 bg-[#3B82F6] hover:bg-[#2563EB] text-white font-semibold rounded-lg transition-all text-sm shadow-lg shadow-blue-500/25" data-testid="cta-demo-btn">
                Try Live Demo
              </Link>
              <a href="mailto:info@sgkapps.com" className="px-8 py-3.5 bg-white/5 hover:bg-white/10 border border-white/15 text-white font-semibold rounded-lg transition-all text-sm">
                Contact Sales
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <WebsiteFooter />
    </div>
  );
}
